'use client'

import React, { useEffect } from 'react'
import { OTPInput as InputOTP, OTPInputContext } from 'input-otp'
import { BaseFieldProps } from '../types'
import { cn, baseInputStyles, inputVariants, labelStyles, errorStyles, helperTextStyles } from '../utils/styles'

export interface OTPInputProps extends BaseFieldProps {
  value: string
  onChange: (value: string) => void
  onComplete?: (value: string) => void
  length?: number
  pasteTransformer?: (value: string) => string
  allowedCharacters?: 'numeric' | 'alphanumeric'
  autoComplete?: boolean
  pushPasswordManagerStrategy?: boolean
}

function Slot({ index, ...props }: React.ComponentPropsWithoutRef<'div'> & { index: number }) {
  const inputOTPContext = React.useContext(OTPInputContext)
  const slot = inputOTPContext.slots[index]

  if (!slot) return null

  const { char, hasFakeCaret, isActive } = slot

  return (
    <div
      className={cn(
        // Base slot styling
        'relative flex h-12 w-12 items-center justify-center border text-sm font-medium transition-all',
        'first:rounded-l-md last:rounded-r-md',
        'focus-within:z-10',
        // Active states
        isActive && 'ring-2 ring-blue-500 ring-offset-2',
        // Border and background
        'border-gray-300 bg-white',
        // Disabled state
        inputOTPContext.isDisabled && 'bg-gray-50 text-gray-400 cursor-not-allowed',
        // Error state
        props.className?.includes('error') && 'border-red-500',
        props.className
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px animate-pulse bg-gray-900" />
        </div>
      )}
    </div>
  )
}

export function OTPInput({
  value,
  onChange,
  onComplete,
  length = 6,
  pasteTransformer,
  allowedCharacters = 'numeric',
  autoComplete = true,
  pushPasswordManagerStrategy = true,
  required = false,
  disabled = false,
  label = 'Verification Code',
  error,
  helperText,
  className
}: OTPInputProps) {
  // Pattern based on allowed characters
  const pattern = allowedCharacters === 'numeric' ? /^\d+$/ : /^[a-zA-Z0-9]+$/

  // Handle completion
  useEffect(() => {
    if (value.length === length && onComplete) {
      onComplete(value)
    }
  }, [value, length, onComplete])

  // Default paste transformer for numeric inputs
  const defaultPasteTransformer = (pastedValue: string): string => {
    // Remove any non-allowed characters and limit to length
    if (allowedCharacters === 'numeric') {
      return pastedValue.replace(/[^\d]/g, '').slice(0, length)
    }
    return pastedValue.replace(/[^a-zA-Z0-9]/g, '').slice(0, length)
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* Label */}
      {label && (
        <label className={labelStyles}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* OTP Input */}
      <InputOTP
        value={value}
        onChange={onChange}
        maxLength={length}
        pattern={pattern.source}
        autoComplete={autoComplete ? 'one-time-code' : 'off'}
        pushPasswordManagerStrategy={pushPasswordManagerStrategy}
        pasteTransformer={pasteTransformer || defaultPasteTransformer}
        disabled={disabled}
        render={({ slots }: { slots: any[] }) => (
          <div className="flex gap-2">
            {slots.slice(0, Math.ceil(length / 2)).map((_: any, index: number) => (
              <Slot
                key={index}
                index={index}
                className={error ? 'error' : ''}
              />
            ))}

            {/* Optional separator for better visual grouping */}
            {length > 4 && (
              <div className="flex items-center">
                <div className="w-px h-4 bg-gray-300" />
              </div>
            )}

            {slots.slice(Math.ceil(length / 2)).map((_: any, index: number) => (
              <Slot
                key={index + Math.ceil(length / 2)}
                index={index + Math.ceil(length / 2)}
                className={error ? 'error' : ''}
              />
            ))}
          </div>
        )}
      />

      {/* Helper text and error */}
      {error && <p className={errorStyles}>{error}</p>}
      {helperText && !error && <p className={helperTextStyles}>{helperText}</p>}
    </div>
  )
}